/**
 * Delete-member confirm dialog (DNKADM-0203) — the admin-only, IRREVERSIBLE GDPR
 * right-to-be-forgotten erase.
 *
 * A purpose-built type-to-confirm gate (no shared one exists): the admin must type the
 * member's name (trimmed, case-insensitive) to enable the destructive **Delete member**
 * button. It calls `eraseMember(contactId)` (no body, bearer-only) and:
 *  - on a 200 (any of `completed`/`partial`/`blocked`) hands the status up via
 *    `onErased` — the member's dink data is gone in all three, so the parent navigates
 *    back to the members list with the outcome message;
 *  - on an error surfaces it INLINE without closing (via {@link toErasureErrorView}).
 *    The `409 ERASURE_BLOCKED_LAST_OWNER` refusal (nothing erased) keeps the member and
 *    points to the `platform-members` role-change surface — the OPPOSITE of the `200
 *    blocked` in-progress outcome.
 *
 * White-label (DNKADM-0044): the contract's "GHL contact" is rendered NEUTRALLY as
 * "marketing contact"; any backend error text flows through the brand-scrub seam.
 */
import { useCallback, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { eraseMember, type MemberErasureStatus } from '@/api/members';
import { toErasureErrorView } from './membersError';

export interface DeleteMemberDialogProps {
  contactId: string;
  /** The member's display name — the string the admin must type to confirm. */
  memberName: string;
  open: boolean;
  onClose: () => void;
  /**
   * Called after a 200 erasure (any status). The member's dink data is gone, so the
   * parent closes the dialog, toasts the outcome, and navigates back to the list.
   */
  onErased: (status: MemberErasureStatus) => void;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string; lastOwner: boolean };

export function DeleteMemberDialog(props: DeleteMemberDialogProps) {
  // Remount the form per open so the typed confirmation + any error reseed cleanly.
  if (!props.open) return null;
  return <DeleteMemberForm {...props} />;
}

function DeleteMemberForm({ contactId, memberName, onClose, onErased }: DeleteMemberDialogProps) {
  const ids = useId();
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const submitting = status.kind === 'submitting';
  const target = memberName.trim();
  const nameMatches = target !== '' && confirmText.trim().toLowerCase() === target.toLowerCase();
  const deleteDisabled = submitting || !nameMatches;

  const handleDelete = useCallback(async () => {
    if (!nameMatches || submitting) return;
    setStatus({ kind: 'submitting' });
    try {
      const result = await eraseMember(contactId);
      onErased(result.status);
    } catch (error) {
      const view = toErasureErrorView(error);
      setStatus({ kind: 'error', message: view.message, lastOwner: view.lastOwner });
    }
  }, [contactId, nameMatches, submitting, onErased]);

  const titleId = `${ids}-title`;
  const confirmId = `${ids}-confirm`;

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>Delete member</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DialogContentText sx={{ fontSize: 14 }}>
            This permanently deletes <strong>{memberName}</strong> under the right to be
            forgotten. It can’t be undone.
          </DialogContentText>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>What happens</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13, '& li': { mb: 0.5 } }}>
              <li>Their login and marketing contact are removed.</li>
              <li>Their photo and profile are deleted.</li>
              <li>
                Their bookings, credits, and payment records are kept but stripped of personal
                identifiers, as financial-record law requires.
              </li>
              <li>No data export or receipt is produced.</li>
            </Box>
          </Box>

          <TextField
            id={confirmId}
            label={`Type “${memberName}” to confirm`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={submitting}
            fullWidth
            size="small"
            slotProps={{
              htmlInput: { 'aria-label': 'Type the member’s name to confirm deletion' },
            }}
          />

          {status.kind === 'error' && (
            <Alert severity="error" role="alert">
              {status.message}
              {status.lastOwner && (
                <Box sx={{ mt: 0.5 }}>
                  <MuiLink component={RouterLink} to="/platform-members">
                    Manage member roles
                  </MuiLink>
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleDelete()}
          disabled={deleteDisabled}
        >
          {submitting ? 'Deleting…' : 'Delete member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
